import { Button, Chip } from '@progress/kendo-react-buttons';
import { Window } from '@progress/kendo-react-dialogs';
import { NumericTextBox, NumericTextBoxChangeEvent } from '@progress/kendo-react-inputs';
import clsx from 'clsx';
import React, { useEffect } from 'react';
import { ChevronDownOutline, ChevronUpOutline } from '../../icons/icons';
import { MessageId } from '../../messages/message-id';
import { MessageType } from '../../services/actor-messages';
import { FlightDebugEntry, FlightDebugEntryType } from '../../webscoket/debug/message-debugger';
import { useAppDispatch, useAppSelector } from '../hooks/store-hooks';
import { useFlightWorker } from '../hooks/use-flight-worker';
import { setSetting } from '../state/slices/settings-slice';

export function MessageDebugger(props: { onClose?: () => void }) {
    const enabled = useAppSelector((x) => x.settings.enableMessageDebugger);
    const debugFlightsIds = useAppSelector((x) => x.settings.debugFlightIds);
    const [selectedFlight, setSelectedFlight] = React.useState<number | null>(null);
    const [draftFlightId, setDraftFlightId] = React.useState<number | null>(null);
    const [messages, setMessages] = React.useState([] as FlightDebugEntry[]);
    const worker = useFlightWorker();
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (selectedFlight === null) {
            setMessages([]);
        } else {
            worker.actor
                .sendAsync({
                    type: MessageType.debugFlightMessagesGetForFlight,
                    data: { flightId: selectedFlight }
                })
                .then((messages) => {
                    setMessages(messages.toReversed());
                });
        }
    }, [selectedFlight]);

    useEffect(() => {
        worker.actor.registerMessageHandler(MessageType.debugFlightMessageAdded, (message) => {
            if (message.flightId === selectedFlight) {
                setMessages((p) => [message, ...p]);
            }
            return Promise.resolve();
        });
        return () => {
            worker.actor.unregisterMessageHandler(MessageType.debugFlightMessageAdded);
        };
    }, [selectedFlight]);

    const addFlight = React.useCallback(() => {
        if (draftFlightId === null) {
            return;
        }

        const flightId = Math.trunc(draftFlightId);
        if (flightId <= 0) {
            return;
        }

        if (debugFlightsIds.includes(flightId)) {
            return;
        }

        dispatch(setSetting('debugFlightIds', [...debugFlightsIds, flightId]));
        setSelectedFlight(flightId);
        setDraftFlightId(null);
        worker.actor.sendAsync({
            type: MessageType.debugFlightMessageSetFlight,
            data: {
                type: 'add',
                flightId
            }
        });
    }, [draftFlightId, debugFlightsIds]);

    const removeFlight = React.useCallback(
        (flightId: number) => {
            dispatch(
                setSetting(
                    'debugFlightIds',
                    debugFlightsIds.filter((item) => item !== flightId)
                )
            );
            worker.actor.sendAsync({
                type: MessageType.debugFlightMessageSetFlight,
                data: {
                    type: 'remove',
                    flightId
                }
            });
        },
        [debugFlightsIds]
    );

    const onDraftFlightChange = React.useCallback((e: NumericTextBoxChangeEvent) => {
        setDraftFlightId(e.value);
    }, []);

    return (
        <Window title={'Message debugger'} onClose={props.onClose} draggable={true} initialHeight={720} initialWidth={820}>
            <div className="d-flex flex-column h-100 gap-3">
                <div className="d-flex flex-column gap-2">
                    <div className="fw-semibold">Watched flights</div>
                    <div className="d-flex flex-wrap gap-2">
                        {debugFlightsIds.map((flightId) => {
                            return (
                                <Chip
                                    className="font-mono"
                                    selected={flightId === selectedFlight}
                                    removable
                                    onRemove={() => removeFlight(flightId)}
                                    onClick={() => setSelectedFlight(flightId)}>
                                    {flightId}
                                </Chip>
                            );
                        })}

                        {!debugFlightsIds.length && <div className="text-muted">No flights watched yet.</div>}
                    </div>
                </div>

                <div className="d-flex gap-2 align-items-end justify-content-between">
                    <div className="d-flex gap-2 align-items-end">
                        <div className="d-flex flex-column gap-1">
                            <label className="fw-semibold">Add flight ID</label>
                            <NumericTextBox
                                className="font-mono"
                                min={1}
                                step={1}
                                format={'#'}
                                value={draftFlightId}
                                onChange={onDraftFlightChange}
                                width={180}
                            />
                        </div>
                        <Button themeColor="primary" onClick={addFlight} disabled={draftFlightId === null}>
                            Add
                        </Button>
                    </div>

                    <Button
                        onClick={() => dispatch(setSetting('enableMessageDebugger', !enabled))}
                        themeColor={enabled ? 'error' : 'success'}>
                        {enabled ? 'Stop' : 'Start'} listening
                    </Button>
                </div>
                <div className="secondary-text">
                    Flight IDs and enable state will be stored local settings. To see all the messages from the start for a given flight add
                    it to watched flights, Start Listening and restart the app.
                </div>
                <div className="d-flex flex-column gap-2 flex-grow-1 overflow-hidden">
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="fw-semibold">
                            {selectedFlight === null ? 'Messages for all watched flights' : `Messages for flight ${selectedFlight}`}
                        </div>
                        <div className="text-muted">{messages.length} entries</div>
                    </div>

                    <div className="d-flex flex-column gap-2 flex-grow-1 overflow-auto">
                        {selectedFlight !== null &&
                            messages.map((entry) => <MessageEntry key={entry.id} flightId={selectedFlight} message={entry} />)}

                        {!messages.length && <div className="border rounded p-4 text-muted">No messages to display yet.</div>}
                    </div>
                </div>
            </div>
        </Window>
    );
}

function MessageEntry(props: { flightId: number; message: FlightDebugEntry }) {
    const [expanded, setExpanded] = React.useState(false);
    const isBinary = props.message.type === FlightDebugEntryType.binary;
    const subType = props.message.type === FlightDebugEntryType.binary ? MessageId[props.message.subType] : '';

    return (
        <div key={props.flightId} className="border rounded d-flex flex-column gap-2 font-mono">
            <div className="d-flex align-items-center gap-2 px-2 flex-wrap">
                <Button
                    svgIcon={expanded ? ChevronUpOutline : ChevronDownOutline}
                    className="button-small"
                    fillMode="flat"
                    onClick={() => setExpanded(!expanded)}
                />
                <span className={clsx('badge rounded-1 px-2', isBinary ? 'text-bg-info' : 'text-bg-success')}>
                    {isBinary ? 'Binary' : 'JSON'}
                </span>
                {subType && <span className="badge rounded-1 px-2 text-bg-warning">{subType}</span>}
                <span className="flex-grow-1 text-end">{props.message.time}</span>
            </div>
            {expanded && (
                <pre
                    className="m-0 px-2 pb-1 rounded"
                    style={{
                        fontSize: 12,
                        overflowX: 'auto'
                    }}>
                    {JSON.stringify(props.message.content, null, 2)}
                </pre>
            )}
        </div>
    );
}
